<template>
  <div class="space-y-6 animate-fade-in pb-10">

    <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
      <div>
        <h1 class="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">Gestión de Usuarios</h1>
        <p class="text-slate-500 dark:text-slate-400 text-sm">Administra el acceso y roles de tus colaboradores.</p>
      </div>
      <button
        @click="openCreate"
        class="flex items-center gap-2 px-5 py-2.5 bg-brand-600 hover:bg-brand-700 text-white font-bold rounded-xl shadow-lg shadow-brand-500/30 transition-all active:scale-95"
      >
        <Plus class="w-5 h-5" /> Nuevo Usuario
      </button>
    </div>

    <div class="bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm flex items-center gap-4">
      <div class="relative flex-1 max-w-md">
        <Search class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          v-model="searchTerm"
          type="text"
          placeholder="Buscar por nombre, correo o documento..."
          class="w-full pl-10 pr-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all"
        >
      </div>
    </div>

    <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
      <div class="overflow-x-auto">
        <table class="w-full text-sm text-left">
          <thead class="text-xs text-slate-500 uppercase bg-slate-50 dark:bg-slate-800/50 border-b border-slate-100 dark:border-slate-800">
            <tr>
              <th class="px-6 py-4 font-semibold">Usuario</th>
              <th class="px-6 py-4 font-semibold">Rol & Sucursal</th>
              <th class="px-6 py-4 font-semibold">Documento</th>
              <th class="px-6 py-4 font-semibold">Estado</th>
              <th class="px-6 py-4 font-semibold text-right">Acciones</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-100 dark:divide-slate-800">

            <tr v-if="isLoading">
              <td colspan="5" class="px-6 py-8 text-center text-slate-500">Cargando usuarios...</td>
            </tr>

            <tr v-else-if="filteredUsers.length === 0">
              <td colspan="5" class="px-6 py-8 text-center text-slate-500">No se encontraron resultados.</td>
            </tr>

            <tr
              v-else
              v-for="user in filteredUsers"
              :key="user.id"
              class="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors group"
            >
              <td class="px-6 py-4">
                <div class="flex items-center gap-3">
                  <img
                    :src="user.photo_url || `https://ui-avatars.com/api/?name=${user.full_name}&background=random`"
                    class="w-10 h-10 rounded-full object-cover border border-slate-200 dark:border-slate-700"
                  />
                  <div>
                    <div class="font-bold text-slate-900 dark:text-slate-100">{{ user.full_name }}</div>
                    <div class="text-xs text-slate-500">{{ user.email }}</div>
                  </div>
                </div>
              </td>

              <td class="px-6 py-4">
                <div class="flex flex-col">
                  <span class="font-medium text-slate-700 dark:text-slate-300">
                    {{ rolesMap[user.role_id] || 'Rol Desconocido' }}
                  </span>
                  <span class="text-xs text-slate-400 flex items-center gap-1 mt-0.5">
                    <Building class="w-3 h-3" />
                    {{ branchesMap[user.fk_branch_id] || 'Sin Sucursal' }}
                  </span>
                </div>
              </td>

              <td class="px-6 py-4 font-mono text-slate-600 dark:text-slate-400">
                {{ user.document || '---' }}
              </td>

              <td class="px-6 py-4">
                <span
                  class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border"
                  :class="Number(user.is_active) === 1
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-100 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800'
                    : 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700'"
                >
                  <span class="w-1.5 h-1.5 rounded-full" :class="Number(user.is_active) === 1 ? 'bg-emerald-500' : 'bg-slate-400'"></span>
                  {{ Number(user.is_active) === 1 ? 'Activo' : 'Inactivo' }}
                </span>
              </td>

              <td class="px-6 py-4 text-right">
                <div class="flex items-center justify-end gap-2">
                  <button
                    @click="openEdit(user)"
                    class="p-2 text-slate-400 hover:text-white hover:bg-brand-50 rounded-lg transition-colors"
                    title="Editar"
                  >
                    <Edit class="w-4 h-4" />
                  </button>
                  <button
                    @click="deleteUser(user)"
                    class="p-2 text-slate-400 hover:text-white hover:bg-red-900 rounded-lg transition-colors"
                    title="Eliminar"
                  >
                    <Trash2 class="w-4 h-4" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="px-6 py-4 border-t border-slate-100 dark:border-slate-800 text-xs text-slate-500 flex justify-between items-center">
        <span>Mostrando {{ filteredUsers.length }} usuarios</span>
      </div>
    </div>

    <UserForm
      :show="showModal"
      :userToEdit="selectedUser"
      @close="showModal = false"
      @saved="loadData"
    />

  </div>
</template>
<script setup>
import { ref, onMounted, computed } from 'vue';
import {
  Search, Plus, MoreVertical, Edit, Trash2,
  UserX, CheckCircle, XCircle, Building
} from 'lucide-vue-next';
import apiClient from '@/api/client';
import UserForm from './Form.vue';
import { SecureStorage } from '@/utils/secureStorage';
import Swal from 'sweetalert2';

// Estado
const users = ref([]);
const rolesMap = ref({});     // Diccionario ID -> Nombre Rol
const branchesMap = ref({});  // Diccionario ID -> Nombre Sucursal
const isLoading = ref(true);
const searchTerm = ref('');
const showModal = ref(false);
const selectedUser = ref(null);

// 1. Cargar Datos Maestros (Usuarios, Roles, Sucursales)
const loadData = async () => {
  isLoading.value = true;
  try {
    const currentUser = SecureStorage.getItem('llevo_user');

    if (!currentUser || !currentUser.company) {
      throw new Error("No se pudo identificar la compañía.");
    }

    const companyId = currentUser.company;

    // Hacemos las 3 peticiones en paralelo para mayor velocidad
    const [resUsers, resRoles, resBranches] = await Promise.all([
      apiClient.get(`/users?company=${companyId}`),
      apiClient.get(`/roles`),
      apiClient.get(`/branches?company=${companyId}`)
    ]);

    // Asignar usuarios
    users.value = Array.isArray(resUsers.data) ? resUsers.data : (resUsers.data.data || []);

    // Crear mapas para acceso rápido (O(1)) en el template
    // Roles
    const rolesArray = Array.isArray(resRoles.data) ? resRoles.data : (resRoles.data.data || []);
    rolesArray.forEach(r => rolesMap.value[r.id] = r.name);

    // Sucursales
    const branchesArray = Array.isArray(resBranches.data) ? resBranches.data : (resBranches.data.data || []);
    branchesArray.forEach(b => branchesMap.value[b.id] = b.name);

  } catch (error) {
    console.error("Error cargando datos:", error);
    Swal.fire({
      toast: true,
      icon: 'error',
      title: 'Error de conexión',
      text: 'No se pudieron cargar los datos.'
    });
  } finally {
    isLoading.value = false;
  }
};

onMounted(loadData);

// 2. Filtrado
const filteredUsers = computed(() => {
  if (!searchTerm.value) return users.value;
  const term = searchTerm.value.toLowerCase();
  return users.value.filter(u =>
    u.full_name?.toLowerCase().includes(term) ||
    u.email?.toLowerCase().includes(term) ||
    u.document?.includes(term)
  );
});

// 3. Modales
const openCreate = () => {
  selectedUser.value = null;
  showModal.value = true;
};

const openEdit = (user) => {
  selectedUser.value = user;
  showModal.value = true;
};

// 4. Eliminar
const deleteUser = (user) => {
  Swal.fire({
    title: '¿Estás seguro?',
    text: `Se eliminará a ${user.full_name}.`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#ef4444',
    confirmButtonText: 'Sí, eliminar',
    cancelButtonText: 'Cancelar'
  }).then(async (result) => {
    if (result.isConfirmed) {
      try {
        await apiClient.delete(`/users/${user.id}`);
        users.value = users.value.filter(u => u.id !== user.id);
        Swal.fire('Eliminado', 'Usuario eliminado.', 'success');
      } catch (error) {
        Swal.fire('Error', 'No se pudo eliminar.', 'error');
      }
    }
  });
};
</script>